import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftSnackbar from "components/SoftSnackbar";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import { API_URL } from "config";
import { useAuth } from "../../../AuthContext";
import { useModulePermissions } from "utils/modulePermissions";

const EditDesignation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthReady, permissions = [], userPermission = [] } = useAuth();
  const { hasModulePermission, canViewField } = useModulePermissions(
    permissions,
    userPermission,
    "designation"
  );

  const [designation, setDesignation] = useState({ name: "" });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const canViewPage = hasModulePermission("designation.pages.edit.view");
  const canSubmit =
    hasModulePermission("designation.actions.update") &&
    hasModulePermission("designation.actions.submit_update");
  const canOpenList =
    hasModulePermission("designation.pages.list.view") &&
    hasModulePermission("designation.actions.open_list");

  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  useEffect(() => {
    if (!isAuthReady) return;

    if (!canViewPage) {
      setPageLoading(false);
      return;
    }

    const fetchDesignation = async () => {
      try {
        setPageLoading(true);
        const token = localStorage.getItem("authToken");
        const response = await fetch(`${API_URL}/designations/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "Failed to fetch designation");

        setDesignation({ name: data.data?.name || "" });
      } catch (error) {
        setSnackbar({
          open: true,
          message: error.message || "Error loading designation",
          color: "error",
        });

        if (error.message?.toLowerCase().includes("not found")) {
          navigate("/designations");
        }
      } finally {
        setPageLoading(false);
      }
    };

    fetchDesignation();
  }, [canViewPage, id, isAuthReady, navigate]);

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!canSubmit) {
      setSnackbar({
        open: true,
        message: "You do not have permission to update designations.",
        color: "error",
      });
      return;
    }

    if (!designation.name.trim()) {
      setSnackbar({ open: true, message: "Designation name is required.", color: "error" });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_URL}/designations/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: designation.name.trim() }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to update designation");

      setSnackbar({
        open: true,
        message: data.message || "Designation updated successfully",
        color: "success",
      });
      setTimeout(() => navigate("/designations"), 1500);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Update failed",
        color: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthReady || pageLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" height="60vh">
          <CircularProgress size={40} color="info" />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h5" fontWeight="bold">
            Edit Designation
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            You do not have permission to edit designations.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftSnackbar
        open={snackbar.open}
        color={snackbar.color}
        icon="notifications"
        title="Designation"
        content={snackbar.message}
        close={closeSnackbar}
        onClose={closeSnackbar}
        time={3000}
      />

      <SoftBox p={3}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftTypography variant="h5" fontWeight="bold">
            Edit Designation
          </SoftTypography>
          {canOpenList && (
            <SoftButton
              size="small"
              color="info"
              onClick={() => navigate("/designations")}
              startIcon={<Icon>list</Icon>}
            >
              Designation List
            </SoftButton>
          )}
        </SoftBox>

        <form onSubmit={handleUpdate}>
          {canViewField("name") && (
            <SoftBox mb={3}>
              <SoftTypography variant="caption" fontWeight="bold" display="block" mb={1}>
                Designation Name
              </SoftTypography>
              <SoftInput
                placeholder="Enter designation name"
                value={designation.name}
                onChange={(e) => setDesignation({ ...designation, name: e.target.value })}
                fullWidth
              />
            </SoftBox>
          )}

          {canSubmit && (
            <SoftBox mt={4}>
              <SoftButton
                type="submit"
                color="info"
                variant="gradient"
                disabled={loading}
                fullWidth
                endIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {loading ? "Processing..." : "Update Designation"}
              </SoftButton>
            </SoftBox>
          )}
        </form>
      </SoftBox>
    </DashboardLayout>
  );
};

export default EditDesignation;
